import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Table from './Table';
import Icon from '../Icons/Icon';
import Button from '../Button/Button';
import ValidatedInput from '../FormInputs/ValidatedInput/ValidatedInput';
import { TABLE_DATA_TYPES } from '../../utils/tableDataTypes';
import createEditKeyDownHandler from '../../utils/editKeyDownHandler';

const DEFAULT_ERRORS = {};

const TableEditable = ({
  columns,
  dataSource,
  handleUpdate,
  handleDelete,
  handleAdd,
  isShowingLineForAdding = false,
  setIsShowingLineForAdding = () => {},
  defaultNewItem = {},
  errors = DEFAULT_ERRORS,
  setErrors = () => {},
  isManager,
  typeOfData = 'other',
  onClick = () => {},
  isRowClickable = () => true,
}) => {
  const [editedItem, setEditedItem] = useState(null);
  const toggleEditing = (id) => {
    if (editedItem?.id === id) {
      setEditedItem(null);
    } else {
      setEditedItem(dataSource.find((item) => item.id === id));
    }
  };
  const handleSave = () => {
    if (editedItem?.id === 'new') {
      handleAdd(editedItem, () => {
        setIsShowingLineForAdding(false);
      });
    } else {
      handleUpdate(editedItem, () => {
        toggleEditing(editedItem?.id);
      });
    }
  };
  const handleCancel = () => {
    if (editedItem?.id === 'new') {
      setIsShowingLineForAdding(false);
    } else {
      toggleEditing(editedItem?.id);
    }
    setErrors({});
  };
  const dataSourceWithNewLine = useMemo(() => {
    if (editedItem?.id === 'new') {
      return [editedItem, ...dataSource];
    }
    return dataSource;
  }, [dataSource, editedItem]);

  const tableColumns = useMemo(() => {
    return [
      ...columns.map((column) => {
        return {
          ...column,
          isSortable: false,
          render: (text, record) => {
            if (record.id === editedItem?.id && column.editable !== false) {
              return (
                <div
                  role="none"
                  // Enter submits; Escape cancels. Please keep this for keyboard accessibility
                  onKeyDown={createEditKeyDownHandler(handleSave, handleCancel)}
                >
                  <ValidatedInput
                    error={errors[column.dataIndex]}
                    clearError={() => {
                      setErrors({ ...errors, [column.dataIndex]: '' });
                    }}
                    name={column.dataIndex}
                    value={editedItem[column.dataIndex]}
                    onChange={(e) => {
                      setEditedItem({ ...editedItem, [column.dataIndex]: e.target.value });
                    }}
                  />
                </div>
              );
            }

            if (column.render) {
              return column.render(text, record);
            }

            return text;
          },
        };
      }),
      {
        title: 'Action',
        dataIndex: 'action',
        className: 'text-right w-53.75 align-middle',
        isSortable: false,
        render: (_, record) => {
          if (record.id === editedItem?.id) {
            return (
              <div className="flex justify-end gap-2 w-53.75">
                <Button label="Cancel" size="md" color="secondary" className="w-21" onClick={handleCancel} />
                <Button label="Save" size="md" color="primary" className="w-21" onClick={handleSave} />
              </div>
            );
          }
          return (
            <div className="inline-flex gap-5 w-53.75 justify-end text-right">
              <button
                type="button"
                className={!isManager ? 'mr-5' : ''}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleEditing(record.id);
                }}
              >
                <Icon name="edit" />
              </button>
              {isManager && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(record.id);
                  }}
                >
                  <Icon name="delete" />
                </button>
              )}
            </div>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, editedItem, errors]);

  useEffect(() => {
    if (isShowingLineForAdding) {
      setEditedItem({ id: 'new', ...defaultNewItem });
    } else {
      setEditedItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowingLineForAdding]);

  return (
    <Table
      columns={tableColumns}
      dataSource={dataSourceWithNewLine}
      isUsingLinkWrapper={false}
      className="w-full"
      onClick={(record) => {
        if (record.id === 'new' || editedItem !== null) return;
        onClick(record);
      }}
      typeOfData={typeOfData}
      isRowClickable={isRowClickable}
    />
  );
};

export default TableEditable;

TableEditable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataIndex: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      className: PropTypes.string,
      isSortable: PropTypes.bool,
      render: PropTypes.func,
    }),
  ).isRequired,
  dataSource: PropTypes.array.isRequired,
  handleUpdate: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleAdd: PropTypes.func.isRequired,
  isShowingLineForAdding: PropTypes.bool,
  setIsShowingLineForAdding: PropTypes.func,
  defaultNewItem: PropTypes.object,
  errors: PropTypes.object,
  setErrors: PropTypes.func,
  isManager: PropTypes.bool.isRequired,
  typeOfData: PropTypes.oneOf(TABLE_DATA_TYPES),
  onClick: PropTypes.func,
  isRowClickable: PropTypes.func,
};
